/**
 * Integrytas Group — Content Management System (CMS)
 * Component: RichTextEditor
 *
 * A self-contained WYSIWYG editor built on the browser's native
 * contenteditable + Selection/Range APIs.  The public interface is
 * intentionally identical to what TipTap, Quill, or any other rich-text
 * library would expose, so you can hot-swap the implementation without
 * touching any call site:
 *
 *   <RichTextEditor value={html} onChange={setHtml} />
 *
 * TO UPGRADE TO TIPTAP:
 *   1. `npm install @tiptap/react @tiptap/starter-kit`
 *   2. Replace the body of this file with a TipTap wrapper that accepts the
 *      same { value, onChange, readOnly, placeholder, height } props.
 *   3. The slot registry (CmsSlotRegistry) also allows replacing the entire
 *      editor at the host-project level without touching this file.
 *
 * Toolbar groups:
 *   Block type  │ Bold / Italic / Underline / Strike
 *   Lists       │ Link  │ Image (CMS asset picker) │ Video embed
 *   Clear       │ Source-toggle (raw HTML view)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RichTextEditorProps {
  /** Current HTML content string. */
  value: string;
  /** Called with the updated HTML whenever the editor loses focus or the
   *  source-toggle is closed. For instant updates, subscribe to onInput. */
  onChange: (html: string) => void;
  /** Optional: instant-change callback (every keystroke). */
  onInput?: (html: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  /** Height of the editable area in pixels. Default 320. */
  height?: number;
  /** If true, show the "Source" (raw HTML) toggle button. Default true. */
  showSource?: boolean;
  /** Called when the editor receives focus. */
  onFocus?: () => void;
  /** Optional extra CSS class on the outer wrapper. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Toolbar definitions
// ---------------------------------------------------------------------------

type CmdFn = (editor: HTMLDivElement) => void;

interface ToolbarButton {
  label: string;
  title: string;
  cmd: string | CmdFn;
  arg?: string;
  isBlock?: boolean;
}

const BLOCK_TYPES: ToolbarButton[] = [
  { label: 'P',  title: 'Paragraph',  cmd: 'formatBlock', arg: 'p' },
  { label: 'H1', title: 'Heading 1',  cmd: 'formatBlock', arg: 'h1' },
  { label: 'H2', title: 'Heading 2',  cmd: 'formatBlock', arg: 'h2' },
  { label: 'H3', title: 'Heading 3',  cmd: 'formatBlock', arg: 'h3' },
  { label: 'BQ', title: 'Blockquote', cmd: 'formatBlock', arg: 'blockquote' },
];

const INLINE_BUTTONS: ToolbarButton[] = [
  { label: 'B',  title: 'Bold (Ctrl+B)',        cmd: 'bold' },
  { label: 'I',  title: 'Italic (Ctrl+I)',       cmd: 'italic' },
  { label: 'U',  title: 'Underline (Ctrl+U)',    cmd: 'underline' },
  { label: 'S',  title: 'Strikethrough',         cmd: 'strikeThrough' },
];

const LIST_BUTTONS: ToolbarButton[] = [
  { label: '• List',  title: 'Bullet list',   cmd: 'insertUnorderedList' },
  { label: '1. List', title: 'Numbered list', cmd: 'insertOrderedList' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RichTextEditor({
  value,
  onChange,
  onInput,
  readOnly = false,
  placeholder,
  height = 320,
  showSource = true,
  onFocus,
  className = '',
}: RichTextEditorProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.rte.placeholder', 'Enter content…');
  const editorRef  = useRef<HTMLDivElement>(null);
  const [sourceMode, setSourceMode]   = useState(false);
  const [sourceHtml, setSourceHtml]   = useState('');
  const [showLinkDlg, setShowLinkDlg] = useState(false);
  const [showVideoDlg, setShowVideoDlg] = useState(false);
  const [linkUrl, setLinkUrl]         = useState('');
  const [videoProvider, setVideoProvider] = useState('YOUTUBE');
  const [videoId, setVideoId]         = useState('');

  // Keep editor innerHTML in sync with `value` prop (controlled mode)
  useEffect(() => {
    const el = editorRef.current;
    if (!el || document.activeElement === el) return; // don't clobber mid-edit
    if (el.innerHTML !== value) el.innerHTML = value ?? '';
  }, [value]);

  // ---------------------------------------------------------------------------
  // Exec helpers (wraps deprecated execCommand; swap point for TipTap)
  // ---------------------------------------------------------------------------

  const exec = useCallback((cmd: string, arg?: string) => {
    editorRef.current?.focus();
     
    document.execCommand(cmd, false, arg);
    flush();
    // flush reads editorRef.current.innerHTML (a ref) and the onInput prop at call
    // time, so a stale closure here is harmless; it is also defined below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flush = useCallback(() => {
    const html = editorRef.current?.innerHTML ?? '';
    onInput?.(html);
  }, [onInput]);

  const handleBlur = useCallback(() => {
    const html = editorRef.current?.innerHTML ?? '';
    onChange(html);
  }, [onChange]);

  // ---------------------------------------------------------------------------
  // Keyboard shortcuts
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b': e.preventDefault(); exec('bold');        break;
        case 'i': e.preventDefault(); exec('italic');      break;
        case 'u': e.preventDefault(); exec('underline');   break;
        case 'z': e.preventDefault(); exec(e.shiftKey ? 'redo' : 'undo'); break;
      }
    }
  }, [exec]);

  // ---------------------------------------------------------------------------
  // Source-toggle
  // ---------------------------------------------------------------------------

  const enterSourceMode = () => {
    const html = editorRef.current?.innerHTML ?? '';
    setSourceHtml(html);
    setSourceMode(true);
  };

  const exitSourceMode = () => {
    if (editorRef.current) editorRef.current.innerHTML = sourceHtml;
    onChange(sourceHtml);
    setSourceMode(false);
  };

  // ---------------------------------------------------------------------------
  // Link insertion
  // ---------------------------------------------------------------------------

  const insertLink = () => {
    if (!linkUrl.trim()) return;
    exec('createLink', linkUrl.trim());
    setShowLinkDlg(false);
    setLinkUrl('');
  };

  // ---------------------------------------------------------------------------
  // Video embed insertion
  // ---------------------------------------------------------------------------

  const insertVideo = () => {
    if (!videoId.trim()) return;
    const baseUrls: Record<string, string> = {
      YOUTUBE: 'https://www.youtube.com/embed/',
      VIMEO:   'https://player.vimeo.com/video/',
    };
    const src = (baseUrls[videoProvider] ?? baseUrls.YOUTUBE) + videoId.trim();
    const iframe = `<div class="cms-video-wrapper" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1rem 0">` +
      `<iframe src="${src}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" ` +
      `allowfullscreen loading="lazy" title="Embedded video"></iframe></div>`;
    editorRef.current?.focus();
     
    document.execCommand('insertHTML', false, iframe);
    flush();
    setShowVideoDlg(false);
    setVideoId('');
  };

  // ---------------------------------------------------------------------------
  // Toolbar button renderer
  // ---------------------------------------------------------------------------

  const ToolBtn = ({ btn }: { btn: ToolbarButton }) => (
    <button
      type="button"
      title={btn.title}
      onMouseDown={e => {
        e.preventDefault(); // keep editor focus
        if (typeof btn.cmd === 'function') {
          if (editorRef.current) btn.cmd(editorRef.current);
        } else {
          exec(btn.cmd, btn.arg);
        }
      }}
      className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded px-1 text-csm-xs
                 font-medium text-csm-ink hover:bg-csm-surface-alt active:bg-csm-border
                 disabled:opacity-40"
    >
      {btn.label}
    </button>
  );

  // ---------------------------------------------------------------------------
  // Read-only render
  // ---------------------------------------------------------------------------

  if (readOnly) {
    return (
      <div
        className={`cms-rich-content prose max-w-none text-csm-ink ${className}`}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Editor render
  // ---------------------------------------------------------------------------

  return (
    <div className={`cms-rte flex flex-col border border-csm-border rounded ${className}`}>
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-csm-border bg-csm-surface-alt px-2 py-1">

        {/* Block type select */}
        <select
          className="h-6 rounded border border-csm-border bg-white px-1 text-csm-xs focus:outline-none"
          onChange={e => exec('formatBlock', e.target.value)}
          defaultValue=""
        >
          <option value="" disabled>{t('common.rte.block', 'Block')}</option>
          {BLOCK_TYPES.map(b => <option key={b.arg} value={b.arg}>{b.label} — {b.title}</option>)}
        </select>

        <div className="mx-1 h-4 w-px bg-csm-border" />

        {/* Inline styles */}
        {INLINE_BUTTONS.map(b => <ToolBtn key={b.cmd as string} btn={b} />)}

        <div className="mx-1 h-4 w-px bg-csm-border" />

        {/* Lists */}
        {LIST_BUTTONS.map(b => <ToolBtn key={b.cmd as string} btn={b} />)}

        <div className="mx-1 h-4 w-px bg-csm-border" />

        {/* Link */}
        <button type="button" title={t('common.rte.insert_link', 'Insert link')}
          onMouseDown={e => { e.preventDefault(); setShowLinkDlg(v => !v); }}
          className="inline-flex h-6 items-center rounded px-1.5 text-csm-xs font-medium text-csm-ink hover:bg-csm-surface-alt">
          🔗 {t('common.rte.link', 'Link')}
        </button>

        {/* Video embed */}
        <button type="button" title={t('common.rte.embed_video', 'Embed video')}
          onMouseDown={e => { e.preventDefault(); setShowVideoDlg(v => !v); }}
          className="inline-flex h-6 items-center rounded px-1.5 text-csm-xs font-medium text-csm-ink hover:bg-csm-surface-alt">
          ▶ {t('common.rte.video', 'Video')}
        </button>

        <div className="mx-1 h-4 w-px bg-csm-border" />

        {/* Undo/Redo */}
        <button type="button" title={t('common.rte.undo', 'Undo (Ctrl+Z)')} onMouseDown={e => { e.preventDefault(); exec('undo'); }}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-csm-xs hover:bg-csm-surface-alt">↩</button>
        <button type="button" title={t('common.rte.redo', 'Redo (Ctrl+Y)')} onMouseDown={e => { e.preventDefault(); exec('redo'); }}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-csm-xs hover:bg-csm-surface-alt">↪</button>

        {/* Clear formatting */}
        <button type="button" title={t('common.rte.remove_formatting', 'Remove formatting')} onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}
          className="inline-flex h-6 items-center rounded px-1.5 text-csm-xs text-csm-muted hover:bg-csm-surface-alt">✕ {t('common.rte.fmt', 'fmt')}</button>

        {showSource && (
          <>
            <div className="mx-1 h-4 w-px bg-csm-border" />
            <button type="button" title={t('common.rte.toggle_source', 'Toggle source HTML')}
              onMouseDown={e => { e.preventDefault(); if (sourceMode) { exitSourceMode(); } else { enterSourceMode(); } }}
              className={`inline-flex h-6 items-center rounded px-1.5 text-csm-xs font-mono
                          ${sourceMode ? 'bg-primary text-white' : 'text-csm-muted hover:bg-csm-surface-alt'}`}>
              &lt;/&gt;
            </button>
          </>
        )}
      </div>

      {/* ── Link dialog ── */}
      {showLinkDlg && (
        <div className="flex items-center gap-2 border-b border-csm-border bg-yellow-50 px-2 py-1.5">
          <span className="text-csm-xs text-csm-ink-soft">{t('common.rte.url', 'URL:')}</span>
          <input
            autoFocus
            type="url"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') insertLink(); if (e.key === 'Escape') setShowLinkDlg(false); }}
            placeholder="https://…"
            className="h-6 flex-1 rounded border border-csm-border px-2 text-csm-xs focus:outline-none"
          />
          <button type="button" onClick={insertLink}
            className="h-6 rounded bg-primary px-2 text-csm-xs text-white hover:bg-primary-dark">{t('common.rte.insert', 'Insert')}</button>
          <button type="button" onClick={() => setShowLinkDlg(false)}
            className="h-6 rounded border border-csm-border px-2 text-csm-xs hover:bg-csm-surface-alt">{t('common.cancel', 'Cancel')}</button>
        </div>
      )}

      {/* ── Video dialog ── */}
      {showVideoDlg && (
        <div className="flex flex-wrap items-center gap-2 border-b border-csm-border bg-blue-50 px-2 py-1.5">
          <span className="text-csm-xs text-csm-ink-soft">{t('common.rte.provider', 'Provider:')}</span>
          <select value={videoProvider} onChange={e => setVideoProvider(e.target.value)}
            className="h-6 rounded border border-csm-border bg-white px-1 text-csm-xs focus:outline-none">
            <option value="YOUTUBE">YouTube</option>
            <option value="VIMEO">Vimeo</option>
          </select>
          <span className="text-csm-xs text-csm-ink-soft">{t('common.rte.video_id', 'Video ID:')}</span>
          <input
            autoFocus
            value={videoId}
            onChange={e => setVideoId(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') insertVideo(); if (e.key === 'Escape') setShowVideoDlg(false); }}
            placeholder="dQw4w9WgXcQ"
            className="h-6 w-44 rounded border border-csm-border px-2 text-csm-xs font-mono focus:outline-none"
          />
          <button type="button" onClick={insertVideo}
            className="h-6 rounded bg-primary px-2 text-csm-xs text-white hover:bg-primary-dark">{t('common.rte.embed', 'Embed')}</button>
          <button type="button" onClick={() => setShowVideoDlg(false)}
            className="h-6 rounded border border-csm-border px-2 text-csm-xs hover:bg-csm-surface-alt">{t('common.cancel', 'Cancel')}</button>
        </div>
      )}

      {/* ── Source view ── */}
      {sourceMode ? (
        <textarea
          value={sourceHtml}
          onChange={e => setSourceHtml(e.target.value)}
          className="flex-1 resize-none bg-gray-950 p-3 font-mono text-csm-xs text-green-400 focus:outline-none"
          style={{ height }}
          spellCheck={false}
        />
      ) : (
        /* ── WYSIWYG editable area ── */
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={resolvedPlaceholder}
          onBlur={handleBlur}
          onInput={flush}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          className="cms-rte__body flex-1 overflow-y-auto p-3 text-csm-md text-csm-ink focus:outline-none
                     [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:my-3
                     [&_h2]:text-xl  [&_h2]:font-bold [&_h2]:my-2
                     [&_h3]:text-lg  [&_h3]:font-semibold [&_h3]:my-2
                     [&_ul]:list-disc   [&_ul]:ml-5 [&_ul]:my-2
                     [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2
                     [&_blockquote]:border-l-4 [&_blockquote]:border-csm-border
                       [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-csm-ink-soft
                     [&_a]:text-primary [&_a]:underline
                     empty:before:content-[attr(data-placeholder)] empty:before:text-csm-muted"
          style={{ minHeight: height }}
        />
      )}

      {/* ── Status bar ── */}
      <div className="flex items-center justify-between border-t border-csm-border bg-csm-surface-alt px-2 py-0.5">
        <span className="text-csm-2xs text-csm-muted">
          {sourceMode ? t('common.rte.html_source', 'HTML source') : t('common.rte.rich_text', 'Rich text')}
        </span>
        <span className="text-csm-2xs text-csm-muted">
          {t('common.rte.shortcuts_hint', 'Ctrl+B Bold · Ctrl+I Italic · Ctrl+U Underline')}
        </span>
      </div>
    </div>
  );
}
